import {useState} from 'react'
import {arrowRightSm} from '@img'
import {Link} from 'react-router'
import {Tab, TabGroup, TabList, TabPanel, TabPanels} from '@headlessui/react'
import {motion} from 'framer-motion'
import {useCategoryTrips, useCategoryCount} from '@/hooks/useTrips'
import {useSidebar} from '@/context/SidebarContext.jsx'
import TripListing from '@/customComponents/uiComponents/global/TripListing.jsx'

const CATEGORIES = [
    {key: 'upcoming', label: 'Upcoming', heading: 'Upcoming Rides'},
    {key: 'ongoing', label: 'On Going', heading: 'On Going Rides'},
    {key: 'completed', label: 'Completed', heading: 'Completed Rides'},
    {key: 'cancelled', label: 'Cancelled', heading: 'Cancelled Rides'},
]

function CategoryPanel({category, heading}) {
    const {isMobile} = useSidebar()
    // isPlaceholderData (true only while switching category/page) — NOT isFetching — drives
    // the list's dim. The 2s background poll flips isFetching every 2s, so binding the dim
    // to it makes the list flash/"bump" on every poll (see UpcomingRides.jsx, same fix).
    const {data, isLoading, isPlaceholderData} = useCategoryTrips(category, {page: 1, perPage: 4})
    const hasMore = data?.pagination ? data.pagination.total > (data.trips?.length || 0) : false
    const footer = hasMore && !isMobile ? (
        <Link to={`/rides/${category}`}
              className="bg-(--bg-grey) rounded-full flex items-center justify-center gap-2 px-6 py-3 mt-4 font-medium text-(--text-primary) hover:bg-(--bg-dark-grey) transition-colors md:w-auto md:ml-auto">
            View All
            <img src={arrowRightSm} alt="" className="w-4 h-4"/>
        </Link>
    ) : null
    return (
        <TripListing
            trips={data?.trips || []}
            heading={heading}
            rideType={category}
            isLoading={isLoading}
            isFetching={isPlaceholderData}
            footer={footer}
        />
    )
}

export default function BookingHistory() {
    const [selectedIndex, setSelectedIndex] = useState(0)
    const {data: ongoingCount} = useCategoryCount('ongoing')
    const {data: upcomingCount} = useCategoryCount('upcoming')
    const {data: completedCount} = useCategoryCount('completed')
    const {data: cancelledCount} = useCategoryCount('cancelled')

    const counts = {
        ongoing: ongoingCount ?? 0,
        upcoming: upcomingCount ?? 0,
        completed: completedCount ?? 0,
        cancelled: cancelledCount ?? 0,
    }
    const selected = CATEGORIES[selectedIndex]

    return (
        <div className="min-w-0 w-full overflow-hidden">
            <TabGroup selectedIndex={selectedIndex} onChange={setSelectedIndex}>
                <TabList as="div" className="border border-gray-300 rounded-full p-1 mb-5 flex gap-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden md:[&>button]:flex-1">
                    {CATEGORIES.map((tab) => (
                        <Tab key={tab.key} className="shrink-0 md:shrink group flex items-center justify-center gap-1.5 max-md:py-2 max-md:px-3 max-md:pr-4 max-md:text-sm py-3 px-2 rounded-full font-medium cursor-pointer transition-colors duration-200 outline-none data-[selected]:bg-(--bg-dark) data-[selected]:text-white text-black whitespace-nowrap">
                            <span className="inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full text-xs font-medium max-md:min-w-4 max-md:h-4 max-md:px-1 max-md:text-[10px] bg-(--bg-dark) text-white group-data-[selected]:bg-(--accent-green-light) group-data-[selected]:text-black">
                                {counts[tab.key]}
                            </span>
                            {tab.label}
                        </Tab>
                    ))}
                </TabList>

                <TabPanels>
                    <motion.div
                        key={selectedIndex}
                        initial={{opacity: 0}}
                        animate={{opacity: 1}}
                        transition={{duration: 0.2, ease: 'easeOut'}}>
                        <TabPanel static>
                            <CategoryPanel category={selected.key} heading={selected.heading}/>
                        </TabPanel>
                    </motion.div>
                </TabPanels>
            </TabGroup>
        </div>
    )
}
